import { useState } from "react";
import { GoogleAuthProvider, signInWithPopup, signInWithRedirect } from "firebase/auth";
import { Button, Callout, Logo } from "@winche/ui";
import { auth } from "../firebase";

/** Google "G" (brand asset, kept in its own colors). */
function GoogleG() {
  return (
    // Google's official "G" (sign-in branding guidelines): not a UI icon.
    // eslint-disable-next-line no-restricted-syntax
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

/**
 * Admin sign-in (audit 2026-10-10, admin-18): centered 360 px card with the
 * Winche mark, a real Google button and an inline error. A blocked popup
 * (Safari iOS) falls back to the redirect flow instead of failing silently.
 */
export function SignInScreen() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signIn = async () => {
    setBusy(true);
    setError(null);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      const code = (err as { code?: string }).code ?? "";
      if (code === "auth/popup-blocked" || code === "auth/operation-not-supported-in-this-environment") {
        await signInWithRedirect(auth, provider).catch(() => setError("Could not open Google sign-in."));
        return;
      }
      if (code !== "auth/popup-closed-by-user" && code !== "auth/cancelled-popup-request") {
        setError(err instanceof Error ? err.message : "Sign-in failed.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sunken p-4">
      <div className="w-full max-w-[360px] rounded-xl border border-border bg-card p-6 shadow-sm">
        <Logo size={28} label="Winche" />
        <h1 className="mt-5 font-display text-headline font-semibold tracking-tight">Winche Admin</h1>
        <p className="mt-1 text-label text-muted-foreground">Restricted to accounts with the admin claim.</p>
        <Button className="mt-6" fullWidth variant="outline" loading={busy} onClick={signIn} leadingIcon={<GoogleG />}>
          Sign in with Google
        </Button>
        {error ? (
          <Callout tone="danger" className="mt-4" live="assertive">
            {error}
          </Callout>
        ) : null}
      </div>
    </div>
  );
}
